import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { studentFetch } from '../utils/authFetch';
import { resolveDeliveryLanguage } from '../utils/language';
import * as THREE from 'three';

const API   = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/* ─── Exam themes ────────────────────────────────────────────────────────── */
const EXAM_THEMES = {
  comprehension_check: {
    primary: '#059669', light: '#D1FAE5', dark: '#065F46',
    gradient: 'linear-gradient(135deg,#059669,#10B981,#0D9488)',
    icon: '📋', label_ar: 'فحص الفهم',         label_en: 'Comprehension Check',
    timerMinutes: 0,
  },
  unit_test: {
    primary: '#1D4ED8', light: '#DBEAFE', dark: '#1E3A5F',
    gradient: 'linear-gradient(135deg,#1D4ED8,#2563EB,#3B82F6)',
    icon: '📘', label_ar: 'اختبار الوحدة',      label_en: 'Unit Test',
    timerMinutes: 30,
  },
  weekly: {
    primary: '#7C3AED', light: '#EDE9FE', dark: '#4C1D95',
    gradient: 'linear-gradient(135deg,#7C3AED,#8B5CF6,#A78BFA)',
    icon: '📅', label_ar: 'الاختبار الأسبوعي',  label_en: 'Weekly Test',
    timerMinutes: 45,
  },
  monthly: {
    primary: '#D97706', light: '#FEF3C7', dark: '#92400E',
    gradient: 'linear-gradient(135deg,#D97706,#F59E0B,#FCD34D)',
    icon: '🏅', label_ar: 'الاختبار الشهري',    label_en: 'Monthly Test',
    timerMinutes: 60,
  },
  comprehensive: {
    primary: '#C2410C', light: '#FFEDD5', dark: '#7C2D12',
    gradient: 'linear-gradient(135deg,#C2410C,#EA580C,#FB923C)',
    icon: '📚', label_ar: 'الاختبار الشامل',    label_en: 'Comprehensive Test',
    timerMinutes: 75,
  },
  challenge: {
    primary: '#DC2626', light: '#FEE2E2', dark: '#7F1D1D',
    gradient: 'linear-gradient(135deg,#DC2626,#EF4444,#F87171)',
    icon: '⚡', label_ar: 'وضع التحدي',          label_en: 'Challenge Mode',
    timerMinutes: 20,
  },
  on_demand: {
    primary: '#0D9488', light: '#CCFBF1', dark: '#134E4A',
    gradient: 'linear-gradient(135deg,#0D9488,#14B8A6,#2DD4BF)',
    icon: '🎯', label_ar: 'اختبار عند الطلب',   label_en: 'On-Demand Quiz',
    timerMinutes: 0,
  },
  demand: {
    id: 'demand',
    label_ar: 'عند الطلب',
    label_en: 'On-Demand',
    icon: '🎯',
    primary:  '#0D9488',
    light:    '#F0FDFA',
    dark:     '#134E4A',
    gradient: 'linear-gradient(135deg,#134E4A,#0D9488,#14B8A6)',
    timerMinutes: 0,
  },
  mid_year: {
    id: 'mid_year',
    label_ar: 'نصف العام',
    label_en: 'Mid-Year',
    icon: '📌',
    primary:  '#EA580C',
    light:    '#FFF7ED',
    dark:     '#7C2D12',
    gradient: 'linear-gradient(135deg,#7C2D12,#C2410C,#F97316)',
    timerMinutes: 90,
  },
  end_year: {
    id: 'end_year',
    label_ar: 'آخر العام',
    label_en: 'End-Year',
    icon: '🏆',
    primary:  '#4338CA',
    light:    '#EFF6FF',
    dark:     '#1E1B4B',
    gradient: 'linear-gradient(135deg,#1E1B4B,#4338CA,#6366F1)',
    timerMinutes: 120,
  },
  memorization: {
    id: 'memorization',
    label_ar: 'اختبار الحفظ',
    label_en: 'Memorization Test',
    icon: '📖',
    primary:  '#7C3AED',
    light:    '#EDE9FE',
    dark:     '#4C1D95',
    gradient: 'linear-gradient(135deg,#4C1D95,#7C3AED,#A78BFA)',
    timerMinutes: 0,
  },
};

/* ─── Arabic option letters ──────────────────────────────────────────────── */
const AR_LETTERS = { A: 'أ', B: 'ب', C: 'ج', D: 'د' };

/* ─── Arabic text cleaner ────────────────────────────────────────────────── */
function cleanArabicText(text) {
  return (text || '').trim()
    .replace(/\b[A-Za-z]{3,}\b/g, '')
    .replace(/  +/g, ' ')
    .trim();
}

/* ─── Shuffle MCQ options (same logic as Group 1 backend) ────────────────── */
function shuffleQuestion(q) {
  if (q.question_type !== 'MCQ') return q;
  try {
    const opts   = q.answer_options || [];
    const ca     = q.correct_answer || q.correct_answer_text || '';
    const wae    = q.wrong_answer_explanations || {};
    if (opts.length !== 4 || !ca) return q;

    const letters  = ['A', 'B', 'C', 'D'];
    const texts    = opts.map(o => (o.includes('. ') ? o.split('. ').slice(1).join('. ') : o));
    let   correctIdx = opts.findIndex(o => o === ca);
    if (correctIdx === -1) {
      const caLetter = ca.trim()[0]?.toUpperCase();
      correctIdx = letters.indexOf(caLetter);
    }
    if (correctIdx === -1) return q;

    const correctText = texts[correctIdx];
    const shuffled    = [...texts].sort(() => Math.random() - 0.5);

    const newOpts    = letters.map((l, i) => `${l}. ${shuffled[i]}`);
    const newCorrIdx = shuffled.indexOf(correctText);
    const newCorrLetter = letters[newCorrIdx];
    const newCA      = newOpts[newCorrIdx];

    const textToExpl = {};
    texts.forEach((t, i) => { if (letters[i] in wae) textToExpl[t] = wae[letters[i]]; });
    const newWAE = {};
    shuffled.forEach((t, i) => {
      const l = letters[i];
      if (l !== newCorrLetter && t in textToExpl) newWAE[l] = textToExpl[t];
    });

    return { ...q, answer_options: newOpts, correct_answer: newCA, correct_answer_text: newCA, wrong_answer_explanations: newWAE };
  } catch {
    return q;
  }
}

/* ─── useCountUp ─────────────────────────────────────────────────────────── */
function useCountUp(target, active, duration = 1200) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = null;
    const step = ts => {
      if (!start) start = ts;
      const prog = Math.min((ts - start) / duration, 1);
      setVal(Math.round(prog * target));
      if (prog < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, active, duration]);
  return val;
}

/* ═══════════════════════════════════════════════════════════════════════════
   QuizScreen
═══════════════════════════════════════════════════════════════════════════ */
export default function QuizScreen() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const navState  = location.state || {};

  /* ── A. REDIRECT GUARD ── */
  const {
    student_id      = '',
    session_id      = '',
    subject         = '',
    unit            = '',
    topic           = '',
    grade           = 7,
    exam_type       = 'comprehension_check',
    questions: rawQs = [],
    delivery_language: _nav_dlang = null,
    return_to       = '/student/subjects',
    resume_navstate = null,
    subject_id      = null,
  } = navState;

  const _studentRaw = (() => { try { return JSON.parse(localStorage.getItem('student_data')); } catch { return null; } })();
  const _subjectObj = { curriculum_type: navState.curriculum_type, subject_name: navState.subject, name: navState.subject };
  const delivery_language = _nav_dlang != null
    ? _nav_dlang
    : resolveDeliveryLanguage(_subjectObj, _studentRaw);

  const theme       = EXAM_THEMES[exam_type] || EXAM_THEMES.comprehension_check;
  const themeRgb    = (() => { const h = theme.primary; const r = parseInt(h.slice(1,3),16); const g = parseInt(h.slice(3,5),16); const b = parseInt(h.slice(5,7),16); return `${r},${g},${b}`; })();
  const lang        = delivery_language;
  const isAr        = lang === 'ar';
  const detectDir   = (text) => /[\u0600-\u06FF]/.test((text || '').trim()[0]) ? 'rtl' : 'ltr';
  const QUESTION_TYPE_LABELS = {
    ar: {
      MCQ:          'اختيار متعدد',
      SA:           'إجابة قصيرة',
      OE:           'إجابة مفتوحة',
      USE_SENTENCE: 'استخدم في جملة',
      TF:           'صح أو خطأ',
      FILL:         'أكمل الفراغ',
      MATCH:        'توصيل',
      ORDER:        'ترتيب',
      MAT:          'توصيل',
      FIB:          'أكمل الفراغ',
      CALC:         'حساب',
      IRAB:         'إعراب',
      TAFSIR:       'تفسير',
      REAL_CONNECT: 'ربط بالواقع',
      EXT:          'استنتاج',
      WA:           'كتابة',
      POEM_FIB:     'إكمال الأبيات',
      POEM_MAT:     'ترتيب الأبيات',
    },
    en: {
      MCQ:          'Multiple Choice',
      SA:           'Short Answer',
      OE:           'Open Answer',
      USE_SENTENCE: 'Use in a Sentence',
      TF:           'True / False',
      FILL:         'Fill in the Blank',
      MATCH:        'Matching',
      ORDER:        'Ordering',
      MAT:          'Matching',
      FIB:          'Fill in the Blank',
      CALC:         'Calculation',
      IRAB:         'Parsing',
      TAFSIR:       'Interpretation',
      REAL_CONNECT: 'Real-World Link',
      EXT:          'Inference',
      WA:           'Writing',
      POEM_FIB:     'Verse Completion',
      POEM_MAT:     'Verse Ordering',
    },
  };
  const studentRaw  = JSON.parse(localStorage.getItem('student_data') || 'null');
  const studentName = localStorage.getItem('jahbath_student_name') || studentRaw?.first_name || '';

  /* ── B. STATE ── */
  const [cur,           setCur]           = useState(0);
  const [answered,      setAnswered]      = useState(false);
  const [selectedLetter,setSelectedLetter]= useState(null);
  const [results,       setResults]       = useState([]);
  const [phase,         setPhase]         = useState('intro');
  const [remSecs,       setRemSecs]       = useState(theme.timerMinutes * 60);
  const [timerActive,   setTimerActive]   = useState(theme.timerMinutes > 0);
  const [revOpen,       setRevOpen]       = useState(false);
  const [grading,       setGrading]       = useState(false);
  const [cardAnim,      setCardAnim]      = useState('enter');
  const [fibAnswer,     setFibAnswer]     = useState('');
  const [gradingOpen,   setGradingOpen]   = useState(false);
  const [answers,       setAnswers]       = useState({});
  const [submitted,     setSubmitted]     = useState({});
  const [feedback,      setFeedback]      = useState({});
  const [fibVals,       setFibVals]       = useState({});
  const correctCount = results.filter(r => r.isCorrect).length;
  const totalQ = (rawQs || []).length || results.length;
  const avgScore = totalQ > 0
    ? Math.round((correctCount / totalQ) * 100)
    : 0;
  const [burstPos,      setBurstPos]      = useState(null);
  const burstRef        = useRef([]);
  const canvasRef       = useRef(null);
  const startTimeRef    = useRef(null);

  /* ── C. SHUFFLE on mount ── */
  const questionsRef = useRef(null);
  if (questionsRef.current === null) {
    questionsRef.current = (rawQs || []).map(shuffleQuestion);
  }
  const questions = questionsRef.current;

  /* ── redirect if no questions ── */
  useEffect(() => {
    if (!student_id || !questions.length) {
      navigate('/student/subjects', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── D. TIMER ── */
  useEffect(() => {
    if (!timerActive) return;
    const id = setInterval(() => {
      setRemSecs(s => {
        if (s <= 1) {
          clearInterval(id);
          setTimerActive(false);
          handleFinish(results);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [timerActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const timerColor = remSecs > 300 ? theme.primary : remSecs > 60 ? '#D97706' : '#DC2626';
  const timerBlink = remSecs <= 60 && timerActive;

  /* ── Three.js background ── */
  const themeHex = parseInt(theme.primary.replace('#',''), 16);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: themeHex, transparent: true, opacity: 0.22, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3]     = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: themeHex, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: themeHex, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    let t3 = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t3 += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t3 * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8)     pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3];     lp[li++] = pp[i * 3 + 1];     lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3];     lp[li++] = pp[j * 3 + 1];     lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3  - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── E. GRADING ── */
  const handleFinish = useCallback(async (finalResults) => {
    if (!finalResults) finalResults = results;
    setGrading(true);
    let correct = 0;
    let scoreSum = 0;
    try {
      const gradeResults = await Promise.all(
        (finalResults || results).map(async ({ question, answer, isCorrect }) => {
          if (exam_type === 'comprehension_check') {
            try {
              const r = await studentFetch(`${API}/session/grade`, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('student_token') || ''}` },
                body:    JSON.stringify({
                  student_id,
                  session_id,
                  question_id:    question.question_id,
                  question_type:  question.question_type || 'MCQ',
                  student_answer: answer,
                  grading_mode:   'automated',
                  delivery_language: lang,
                  subject_id,
                }),
              });
              if (!r) return { question, answer, isCorrect, score: isCorrect ? 100 : 0 };
              const d = await r.json();
              // null means deferred (awaiting_final_grading) — keep as null, not as false
              const serverCorrect = d?.payload?.grading_status === 'awaiting_final_grading'
                ? null
                : (d?.payload?.is_correct ?? isCorrect);
              const score         = d?.payload?.score      ?? (isCorrect ? 100 : 0);
              return { question, answer, isCorrect: serverCorrect, score };
            } catch {
              return { question, answer, isCorrect, score: isCorrect ? 100 : 0 };
            }
          } else {
            // Pre-built quizzes (unit_test, weekly, monthly, etc.) — questions are not
            // in questions_log so /session/grade would fail. Grade locally and save weakness.
            if (!isCorrect) {
              studentFetch(`${API}/session/save-weakness`, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('student_token') || ''}` },
                body:    JSON.stringify({
                  student_id,
                  subject_id,
                  question_id:        question.question_id,
                  learning_objective: question.learning_objective || '',
                  weakness_type:      'conceptual',
                }),
              }).catch(() => {});
            }
            return { question, answer, isCorrect, score: isCorrect ? 100 : 0 };
          }
        })
      );

      gradeResults.forEach(r => {
        if (r.isCorrect) correct++;
        scoreSum += r.score;
      });

      const avg = gradeResults.length > 0 ? scoreSum / gradeResults.length : 0;

      /* CHANGE 4 — session-complete for comprehension_check */
      if (exam_type === 'comprehension_check') {
        try {
          await studentFetch(`${API}/student/session-complete`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('student_token') || ''}` },
            body:    JSON.stringify({
              student_id,
              session_id,
              subject,
              unit,
              score: avg,
              concepts: [{ topic, score: Math.round(avg) }],
            }),
          });
        } catch { /* non-blocking */ }
      }
    } catch { /* grading failed — avgScore derived from local results */ }
    setGrading(false);
    setPhase('results');
  }, [results, student_id, session_id, lang, subject_id, subject, unit, topic, exam_type]);

  /* ── F2. QUIZ-COMPLETE — fire API then navigate to results page ── */
  useEffect(() => {
    if (phase !== 'results') return;

    if (exam_type !== 'comprehension_check') {
      const wrongObjs = results
        .filter(r => !r.isCorrect)
        .map(r => {
          const lo = r.question?.learning_objective;
          if (!lo) return '';
          return typeof lo === 'object' ? (lo?.description || '') : lo;
        })
        .filter(Boolean);

      studentFetch(`${API}/student/quiz-complete`, {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${localStorage.getItem('student_token') || ''}`,
        },
        body: JSON.stringify({
          student_id:       student_id,
          quiz_id:          session_id,
          session_id:       session_id,
          exam_type:        exam_type,
          subject:          subject,
          subject_id:       subject_id,
          correct_count:    correctCount,
          total_count:      questions.length,
          score_percentage: avgScore,
          xp_earned:        correctCount * 10 + (avgScore >= 80 ? 15 : 0),
          wrong_objectives: wrongObjs,
        }),
      }).catch(() => {});
    }

    const elapsed = startTimeRef.current
      ? Math.floor((Date.now() - startTimeRef.current) / 1000)
      : 0;

    navigate('/student/results', {
      state: {
        fromQuiz:     true,
        score:        avgScore,
        correct:      correctCount,
        wrong:        questions.length - correctCount,
        total:        questions.length,
        time_seconds: elapsed,
        quiz_type:    exam_type,
        subject_name: subject,
        xp_earned:    xpEarned,
        return_to:         return_to || '/student/subjects',
        results:           results,
        delivery_language: delivery_language || 'ar',
      },
    });
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── F. PICK ANSWER ── */
  function handlePick(letter, e) {
    if (answered) return;
    setAnswered(true);
    setSelectedLetter(letter);

    const q = questions[cur];
    const wae = q.wrong_answer_explanations || {};
    const isCorrect = !(letter in wae);

    /* burst from option element */
    if (e?.currentTarget) {
      const rect = e.currentTarget.getBoundingClientRect();
      setBurstPos({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2, correct: isCorrect });
      setTimeout(() => setBurstPos(null), 800);
    }

    const newResults = [...results, { question: q, answer: letter, isCorrect }];
    setResults(newResults);

    const delay = isCorrect ? 1400 : 2600;
    setTimeout(() => {
      if (cur + 1 >= questions.length) {
        handleFinish(newResults);
      } else {
        setCardAnim('exit');
        setTimeout(() => {
          setCur(c => c + 1);
          setAnswered(false);
          setSelectedLetter(null);
          setFibAnswer('');
          setCardAnim('enter');
        }, 320);
      }
    }, delay);
  }

  /* ── F2. TF ANSWER ── */
  function handleTF(choice) {
    if (answered) return;
    const q = questions[cur];
    const correctAnswer = (q.correct_answer || '').toString().trim().toLowerCase();
    const isCorrect = choice.toLowerCase() === correctAnswer ||
      (choice === 'True'  && ['true','صح','yes','correct'].includes(correctAnswer)) ||
      (choice === 'False' && ['false','خطأ','no','incorrect'].includes(correctAnswer));
    setAnswered(true);
    setSelectedLetter(choice);
    const newResults = [...results, { question: q, answer: choice, isCorrect }];
    setResults(newResults);
    const delay = isCorrect ? 1400 : 2600;
    setTimeout(() => {
      if (cur + 1 >= questions.length) { handleFinish(newResults); }
      else {
        setCardAnim('exit');
        setTimeout(() => { setCur(c => c+1); setAnswered(false); setSelectedLetter(null); setFibAnswer(''); setCardAnim('enter'); }, 320);
      }
    }, delay);
  }

  /* ── F3. FIB ANSWER ── */
  function handleFIB() {
    if (answered || !fibAnswer.trim()) return;
    const q = questions[cur];
    const correct = (q.correct_answer || '').toString().trim().toLowerCase();
    const student = fibAnswer.trim().toLowerCase();
    const normalizeNum = (s) => {
      const arabicToEn = s.replace(/[٠١٢٣٤٥٦٧٨٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));
      const n = parseFloat(arabicToEn.replace(/,/g, ''));
      return isNaN(n) ? s.trim().toLowerCase() : Math.round(n * 100) / 100;
    };
    const normStudent = normalizeNum(student);
    const normCorrect = normalizeNum(correct);
    const isCorrect = normStudent === normCorrect ||
      (typeof normStudent === 'number' && typeof normCorrect === 'number' && Math.abs(normStudent - normCorrect) < 0.01) ||
      student === correct ||
      correct.toLowerCase().includes(student) ||
      student.includes(correct.toLowerCase());
    setAnswered(true);
    setSelectedLetter(fibAnswer);
    const newResults = [...results, { question: q, answer: fibAnswer, isCorrect }];
    setResults(newResults);
    const delay = isCorrect ? 1400 : 2600;
    setTimeout(() => {
      if (cur + 1 >= questions.length) { handleFinish(newResults); }
      else {
        setCardAnim('exit');
        setTimeout(() => { setCur(c => c+1); setAnswered(false); setSelectedLetter(null); setFibAnswer(''); setCardAnim('enter'); }, 320);
      }
    }, delay);
  }

  /* ── F4. OPEN ANSWER (SA/OE/etc — server-side grading) ── */
  async function handleOpenAnswer() {
    if (answered || !fibAnswer.trim()) return;
    const q = questions[cur];
    setGradingOpen(true);
    setSelectedLetter(fibAnswer);
    let isCorrect = false;
    try {
      const res = await studentFetch(`${API}/session/grade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('student_token') || ''}` },
        body: JSON.stringify({
          student_id,
          session_id,
          question_id:        q.question_id,
          question_type:      q.question_type,
          student_answer:     fibAnswer,
          correct_answer:     q.correct_answer,
          grading_mode:       'ai_evaluated',
          learning_objective: q.learning_objective_id || '',
        }),
      });
      if (!res) return;
      const data = await res.json();
      isCorrect = data?.payload?.is_correct ?? false;
    } catch {
      isCorrect = false;
    }
    setGradingOpen(false);
    setAnswered(true);
    const newResults = [...results, { question: q, answer: fibAnswer, isCorrect }];
    setResults(newResults);
    const delay = isCorrect ? 1400 : 2600;
    setTimeout(() => {
      if (cur + 1 >= questions.length) { handleFinish(newResults); }
      else {
        setCardAnim('exit');
        setTimeout(() => { setCur(c => c+1); setAnswered(false); setSelectedLetter(null); setFibAnswer(''); setCardAnim('enter'); }, 320);
      }
    }, delay);
  }

  /* ── G2. SCROLL-VIEW HANDLERS ── */
  function handleScrollAnswer(qIdx, answer, isCorrect) {
    if (submitted[qIdx]) return;
    setAnswers(prev => ({ ...prev, [qIdx]: answer }));
    setSubmitted(prev => ({ ...prev, [qIdx]: true }));
    setFeedback(prev => ({ ...prev, [qIdx]: isCorrect }));
  }

  async function handleScrollFinish() {
    if (!IS_BATCH_EXAM) {
      const compiled = questions.map((q, i) => ({
        question: q,
        answer:    answers[i] ?? '',
        isCorrect: feedback[i] ?? false,
      }));
      setResults(compiled);
      await handleFinish(compiled);
      return;
    }

    setGrading(true);
    const batchAnswers = questions.map((q, i) => ({
      question_id:        q.question_id,
      question_type:      q.question_type,
      question_text:      q.question_text || '',
      correct_answer:     q.correct_answer || '',
      student_answer:     answers[i] || '',
      grading_mode:       ['MCQ', 'TF', 'FIB', 'MAT', 'POEM_FIB', 'POEM_MAT', 'CALC', 'VIS'].includes(q.question_type) ? 'automated' : 'ai_evaluated',
      learning_objective: q.learning_objective_id || '',
      grade:              String(grade || ''),
    }));

    try {
      const res = await studentFetch(`${API}/session/finalize-quiz`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('student_token') || ''}` },
        body: JSON.stringify({
          student_id,
          session_id,
          exam_type,
          subject_id:        subject_id ?? null,
          subject,
          delivery_language: lang,
          answers:           batchAnswers,
        }),
      });
      const data = await res.json();
      const gradeResults = data?.payload?.results || [];

      const resultsForScreen = questions.map((q, i) => ({
        question:  q,
        answer:    answers[i] || '',
        isCorrect: gradeResults[i]?.is_correct ?? null,  // null = pending (deferred essay)
        score:     gradeResults[i]?.score ?? 0,
        feedback:  gradeResults[i]?.feedback ?? '',
        correctAnswer: gradeResults[i]?.correct_answer ?? '',
      }));

      // Exclude null (pending/deferred) from both numerator and denominator
      const gradedResults = gradeResults.filter(r => r.is_correct !== null);
      const correct  = gradedResults.filter(r => r.is_correct).length;
      const total    = gradedResults.length || gradeResults.length;  // fallback avoids /0
      const scorePct = total ? Math.round((correct / total) * 100) : 0;
      const xp       = correct * 10 + (scorePct >= 80 ? 15 : 0);

      const wrongObjs = resultsForScreen
        .filter(r => !r.isCorrect)
        .map(r => {
          const lo = r.question?.learning_objective;
          if (!lo) return '';
          return typeof lo === 'object' ? (lo?.description || '') : lo;
        })
        .filter(Boolean);

      studentFetch(`${API}/student/quiz-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('student_token') || ''}` },
        body: JSON.stringify({
          student_id,
          quiz_id:          session_id,
          session_id,
          exam_type,
          subject,
          subject_id,
          correct_count:    correct,
          total_count:      total,
          score_percentage: scorePct,
          xp_earned:        xp,
          wrong_objectives: wrongObjs,
        }),
      }).catch(() => {});

      const elapsed = startTimeRef.current
        ? Math.floor((Date.now() - startTimeRef.current) / 1000)
        : 0;

      navigate('/student/results', {
        state: {
          fromQuiz:          true,
          score:             scorePct,
          correct,
          wrong:             total - correct,
          total,
          time_seconds:      elapsed,
          quiz_type:         exam_type,
          subject_name:      subject,
          xp_earned:         xp,
          return_to:         return_to || '/student/subjects',
          results:           resultsForScreen,
          delivery_language: lang,
        },
      });
    } catch (err) {
      console.error('Finalize quiz failed:', err);
      setGrading(false);
      alert(isAr ? 'حدث خطأ في التصحيح. يرجى المحاولة مرة أخرى.' : 'Grading failed. Please try again.');
    }
  }

  /* ── Score count-up (results phase) ── */
  const displayScore  = useCountUp(Math.round(avgScore),       phase === 'results', 1400);
  const displayCorrect= useCountUp(correctCount,               phase === 'results', 1000);
  const displayWrong  = useCountUp(questions.length - correctCount, phase === 'results', 1000);
  const displayTotal  = useCountUp(questions.length,           phase === 'results', 800);
  const xpEarned      = correctCount * 10 + (avgScore >= 80 ? 15 : 0);

  /* ── Timer display ── */
  const timerMins = String(Math.floor(remSecs / 60)).padStart(2, '0');
  const timerSec  = String(remSecs % 60).padStart(2, '0');
  const totalSecs = theme.timerMinutes * 60 || 1;
  const timerArc  = ((totalSecs - remSecs) / totalSecs) * 251.2; // circumference ~251

  /* ─────────────────────────────────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────────────────────────────────── */
  if (!questions.length) return null;

  const q = questions[cur];
  const wae = q.wrong_answer_explanations || {};
  const displayText = lang === 'ar' ? cleanArabicText(q.question_text) : q.question_text;

  /* ── Helper: option state ── */
  function optionState(letter) {
    if (!answered) return 'default';
    if (letter === selectedLetter) return letter in wae ? 'wrong' : 'correct';
    if (!(letter in wae) && answered) return 'correct-reveal';
    return 'dimmed';
  }

  function optionStyle(letter) {
    const s = optionState(letter);
    const base = {
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '14px 20px', borderRadius: 18, marginBottom: 10,
      cursor: answered ? 'default' : 'pointer',
      border: '2px solid #E5E7EB',
      background: '#fff',
      boxShadow: '0 4px 0 #E5E7EB',
      transition: 'transform .12s, box-shadow .12s, border-color .12s',
      direction: isAr ? 'rtl' : 'ltr',
      userSelect: 'none',
    };
    if (s === 'correct' || s === 'correct-reveal') return { ...base, background: 'linear-gradient(135deg,#D1FAE5,#A7F3D0)', border: '2px solid #059669', boxShadow: '0 4px 0 rgba(5,150,105,.3)', transform: 'scale(1.02) translateY(-2px)' };
    if (s === 'wrong')   return { ...base, background: 'linear-gradient(135deg,#FEE2E2,#FECACA)', border: '2px solid #DC2626', boxShadow: '0 4px 0 rgba(220,38,38,.2)', animation: 'qs-shake 0.4s ease' };
    if (s === 'dimmed')  return { ...base, opacity: 0.3 };
    return base;
  }

  /* ─── INTRO PHASE ─── */
  if (phase === 'intro') {
    const diffCounts = {};
    questions.forEach(q => { const d = q.difficulty || ''; if (d) diffCounts[d] = (diffCounts[d] || 0) + 1; });
    const typeGroups = {};
    questions.forEach(q => { const t = q.question_type || 'MCQ'; typeGroups[t] = (typeGroups[t] || 0) + 1; });

    return (
      <div style={{ ...S.page, alignItems: 'center', overflowY: 'auto' }}>
        <canvas ref={canvasRef} style={S.canvas} />
        <style>{GLOBAL_CSS}</style>
        <div style={{ width: '100%', maxWidth: 640, margin: '0 auto', padding: '0 16px 60px', zIndex: 1 }}>

          {/* Gradient header */}
          <div style={{ background: theme.gradient, borderRadius: '0 0 32px 32px', padding: '52px 28px 56px', textAlign: 'center', color: '#fff' }}>
            <div style={{ fontSize: 56, marginBottom: 12 }}>{theme.icon}</div>
            <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 6 }}>
              {isAr ? theme.label_ar : theme.label_en}
            </div>
            <div style={{ fontSize: 15, opacity: 0.85, marginBottom: 4 }}>{subject}</div>
            {unit && <div style={{ fontSize: 13, opacity: 0.7 }}>{unit}</div>}
          </div>

          {/* Stats row */}
          <div style={{ display: 'flex', gap: 12, background: '#fff', borderRadius: 24, border: '2px solid #E5E7EB', padding: '20px 24px', margin: '-30px 8px 0', boxShadow: '0 5px 0 #E5E7EB, 0 12px 32px rgba(0,0,0,.07)', position: 'relative', zIndex: 2 }}>
            {[
              { label: isAr ? 'عدد الأسئلة' : 'Questions', val: totalQ, color: theme.primary },
              { label: isAr ? 'الوقت'       : 'Time',      val: theme.timerMinutes > 0 ? `${theme.timerMinutes}m` : (isAr ? 'غير محدد' : 'No limit'), color: '#7C3AED' },
              { label: isAr ? 'الصف'        : 'Grade',     val: grade, color: '#D97706' },
            ].map(({ label, val, color }) => (
              <div key={label} style={{ flex: 1, textAlign: 'center', padding: '8px 0', borderRadius: 12, background: '#FAFAFA', border: `2px solid ${color}30` }}>
                <div style={{ fontSize: 26, fontWeight: 800, color }}>{val}</div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Instructions */}
          <div style={{ background: '#fff', borderRadius: 20, border: '2px solid #E5E7EB', padding: '20px 24px', marginTop: 16, boxShadow: '0 4px 0 #E5E7EB' }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937', marginBottom: 12, direction: isAr ? 'rtl' : 'ltr' }}>
              {isAr ? '📋 تعليمات الاختبار' : '📋 Instructions'}
            </div>
            <ul style={{ margin: 0, padding: isAr ? '0 20px 0 0' : '0 0 0 20px', color: '#374151', fontSize: 14, lineHeight: 1.9, direction: isAr ? 'rtl' : 'ltr' }}>
              <li>{isAr ? 'اقرأ كل سؤال بعناية قبل الإجابة.' : 'Read each question carefully before answering.'}</li>
              <li>{isAr ? 'يمكنك التمرير للأعلى والأسفل بين الأسئلة.' : 'You can scroll up and down between questions freely.'}</li>
              {theme.timerMinutes > 0 && (
                <li>{isAr ? `لديك ${theme.timerMinutes} دقيقة لإنهاء الاختبار.` : `You have ${theme.timerMinutes} minutes to complete the quiz.`}</li>
              )}
              <li>{isAr ? 'اضغط "إنهاء الاختبار" بعد الإجابة على جميع الأسئلة.' : 'Press "Finish Quiz" after answering all questions.'}</li>
            </ul>
          </div>

          {/* Difficulty breakdown */}
          {Object.keys(diffCounts).length > 0 && (
            <div style={{ background: '#fff', borderRadius: 20, border: '2px solid #E5E7EB', padding: '16px 24px', marginTop: 16, boxShadow: '0 4px 0 #E5E7EB' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#374151', marginBottom: 10, direction: isAr ? 'rtl' : 'ltr' }}>
                {isAr ? '📊 توزيع مستوى الصعوبة' : '📊 Difficulty Breakdown'}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.entries(diffCounts).map(([diff, cnt]) => {
                  const color = diff === 'easy' ? '#059669' : diff === 'hard' ? '#DC2626' : diff === 'challenge' ? '#991B1B' : '#D97706';
                  const label = isAr
                    ? (diff === 'easy' ? 'سهل' : diff === 'hard' ? 'صعب' : diff === 'challenge' ? 'تحدي' : 'متوسط')
                    : (diff === 'easy' ? 'Easy' : diff === 'hard' ? 'Hard' : diff === 'challenge' ? 'Challenge' : 'Medium');
                  return (
                    <div key={diff} style={{ background: `${color}18`, border: `1.5px solid ${color}40`, borderRadius: 99, padding: '4px 14px', fontSize: 13, fontWeight: 600, color }}>
                      {label}: {cnt}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Question type breakdown */}
          {Object.keys(typeGroups).length > 1 && (
            <div style={{ background: '#fff', borderRadius: 20, border: '2px solid #E5E7EB', padding: '16px 24px', marginTop: 16, boxShadow: '0 4px 0 #E5E7EB' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#374151', marginBottom: 10 }}>
                {isAr ? '📝 أنواع الأسئلة' : '📝 Question Types'}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.entries(typeGroups).map(([type, cnt]) => (
                  <div key={type} style={{ background: theme.light, border: `1.5px solid ${theme.primary}40`, borderRadius: 99, padding: '4px 14px', fontSize: 13, fontWeight: 600, color: theme.dark }}>
                    {(QUESTION_TYPE_LABELS[delivery_language]?.[type] || type)}: {cnt}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Start button */}
          <button
            style={{ ...S.primaryBtn, background: theme.gradient, border: `2px solid ${theme.primary}40`, boxShadow: `0 5px 0 ${theme.primary}40, 0 8px 20px ${theme.primary}30`, width: '100%', marginTop: 24, fontSize: 18 }}
            onClick={() => { startTimeRef.current = Date.now(); setPhase('quiz'); }}>
            {isAr ? 'ابدأ الاختبار ←' : '→ Start Quiz'}
          </button>

          {/* Back */}
          <button
            style={{ ...S.secondaryBtn, width: '100%', marginTop: 10 }}
            onClick={() => navigate(return_to || '/student/subjects')}>
            {isAr ? '← العودة' : '← Back'}
          </button>
        </div>
      </div>
    );
  }

  /* ─── QUIZ PHASE (scroll view — all questions at once) ─── */
  const OPEN_TYPES = ['SA','OE','USE_SENTENCE','IRAB','TAFSIR','REAL_CONNECT','EXT','WA'];
  const IS_BATCH_EXAM = exam_type !== 'comprehension_check';
  const answeredCount = Object.keys(submitted).filter(k => submitted[k]).length;

  return (
    <div style={S.page} dir={isAr ? 'rtl' : 'ltr'}>
      <canvas ref={canvasRef} style={S.canvas} />
      <style>{GLOBAL_CSS}</style>

      {/* ── G. HEADER ── */}
      <header style={S.header}>
        <div style={S.logo}>
          <span style={{ color: '#1E3A5F', fontWeight: 800, fontFamily: "'Sora',sans-serif" }}>Jah</span>
          <span style={{ color: '#059669', fontWeight: 800, fontFamily: "'Sora',sans-serif" }}>bath</span>
        </div>
        <div style={S.chips}>
          <span style={{ ...S.chip, background: theme.light, color: theme.dark }}>
            {theme.icon} {isAr ? theme.label_ar : theme.label_en}
          </span>
          <span style={{ ...S.chip, background: '#EFF6FF', color: '#1D4ED8' }}>
            {isAr ? `الصف ${grade}` : `Grade ${grade}`}
          </span>
          <span style={{ ...S.chip, background: '#F5F3FF', color: '#7C3AED' }}>{subject}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={S.avatar}>{(studentName || 'ST').slice(0, 2).toUpperCase()}</div>
          <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>{studentName}</span>
          <button style={S.exitBtn} onClick={() => navigate(return_to || '/student/subjects')}>
            {isAr ? '✕ خروج' : '✕ Exit'}
          </button>
        </div>
      </header>

      {/* ── H. STRIP ── */}
      <div className="qs-strip" style={S.strip}>
        {theme.timerMinutes > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, animation: timerBlink ? 'qs-blink 0.8s infinite' : 'none' }}>
            <svg width="48" height="48" viewBox="0 0 48 48">
              <circle cx="24" cy="24" r="20" fill="none" stroke="#E5E7EB" strokeWidth="4" />
              <circle cx="24" cy="24" r="20" fill="none" stroke={timerColor} strokeWidth="4"
                strokeDasharray="125.66" strokeDashoffset={timerArc} strokeLinecap="round" transform="rotate(-90 24 24)" />
            </svg>
            <span style={{ fontWeight: 700, fontSize: 15, color: timerColor, fontVariantNumeric: 'tabular-nums' }}>
              {timerMins}:{timerSec}
            </span>
          </div>
        )}
        <div style={{ flex: 1, height: 10, background: '#E5E7EB', borderRadius: 99, overflow: 'hidden', boxShadow: 'inset 0 1px 3px rgba(0,0,0,.1)' }}>
          <div style={{ height: '100%', borderRadius: 99, width: `${(answeredCount / totalQ) * 100}%`, background: theme.gradient, transition: 'width 0.4s ease', backgroundSize: '200% 100%', animation: 'qs-shimmer 2s linear infinite' }} />
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, color: theme.primary, whiteSpace: 'nowrap' }}>
          {answeredCount}/{totalQ}
        </span>
      </div>

      {/* ── I. SCROLL CONTENT — all questions ── */}
      <div className="qs-content-col" style={{ flex: 1, overflowY: 'auto', padding: '24px 16px 120px' }}>
        {questions.map((question, qIdx) => {
          const qWae        = question.wrong_answer_explanations || {};
          const correctLetter = (question.correct_answer || '').toString().trim().charAt(0).toUpperCase();
          const qOpts       = question.answer_options || [];
          const qDisplayTxt = lang === 'ar' ? cleanArabicText(question.question_text) : question.question_text;
          const qLoText     = typeof question.learning_objective === 'object'
            ? (question.learning_objective?.description || '')
            : (question.learning_objective || '');
          const isSubmitted = !!submitted[qIdx];
          const selectedAns = answers[qIdx];
          const isQCorrect  = feedback[qIdx];
          const fibVal      = fibVals[qIdx] || '';

          const cardHeaderBg = isSubmitted
            ? (IS_BATCH_EXAM ? theme.gradient : (isQCorrect ? 'linear-gradient(135deg,#059669,#10B981)' : 'linear-gradient(135deg,#DC2626,#EF4444)'))
            : theme.gradient;

          function qOptState(letter) {
            if (!isSubmitted) return 'default';
            if (IS_BATCH_EXAM) return letter === selectedAns ? 'selected' : 'default';
            if (letter === selectedAns) return letter === correctLetter ? 'correct' : 'wrong';
            if (letter === correctLetter) return 'correct-reveal';
            return 'dimmed';
          }

          function qOptStyle(letter) {
            const s = qOptState(letter);
            const base = {
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '14px 20px', borderRadius: 18, marginBottom: 10,
              cursor: isSubmitted ? 'default' : 'pointer',
              border: '2px solid #E5E7EB', background: '#fff',
              boxShadow: '0 4px 0 #E5E7EB', transition: 'transform .12s',
              direction: isAr ? 'rtl' : 'ltr', userSelect: 'none',
            };
            if (s === 'selected')
              return { ...base, background: '#EFF6FF', border: '2px solid #3B82F6', boxShadow: '0 4px 0 rgba(59,130,246,.3)' };
            if (s === 'correct' || s === 'correct-reveal')
              return { ...base, background: 'linear-gradient(135deg,#D1FAE5,#A7F3D0)', border: '2px solid #059669', boxShadow: '0 4px 0 rgba(5,150,105,.3)' };
            if (s === 'wrong')
              return { ...base, background: 'linear-gradient(135deg,#FEE2E2,#FECACA)', border: '2px solid #DC2626', boxShadow: '0 4px 0 rgba(220,38,38,.2)' };
            if (s === 'dimmed') return { ...base, opacity: 0.3 };
            return base;
          }

          async function submitFib(val) {
            if (!val.trim() || isSubmitted) return;
            if (OPEN_TYPES.includes(question.question_type)) {
              if (!IS_BATCH_EXAM) {
                try {
                  const res = await studentFetch(`${API}/session/grade`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('student_token') || ''}` },
                    body: JSON.stringify({
                      student_id,
                      session_id,
                      question_id:        question.question_id,
                      question_type:      question.question_type,
                      student_answer:     val,
                      grading_mode:       'ai_evaluated',
                      question_text:      question.question_text || '',
                      learning_objective: question.learning_objective_id || '',
                      subject,
                      grade,
                      delivery_language:  lang,
                      subject_id:         subject_id ?? null,
                    }),
                  });
                  const data = await res.json();
                  // awaiting_final_grading = deferred essay — pass null (PENDING), never false
                  const isCorrect = data?.payload?.grading_status === 'awaiting_final_grading'
                    ? null
                    : (data?.payload?.is_correct ?? false);
                  handleScrollAnswer(qIdx, val, isCorrect);
                } catch (err) {
                  console.error('Grading failed:', err);
                  handleScrollAnswer(qIdx, val, null);
                }
              } else {
                handleScrollAnswer(qIdx, val, null);
              }
              return;
            }
            // CALC: use final_answer (bare numeric) not correct_answer (full working paragraph)
            const correct = (question.question_type === 'CALC'
              ? (question.final_answer || question.correct_answer || '')
              : (question.correct_answer || '')).toString().trim().toLowerCase();
            const student = val.trim().toLowerCase();
            const normalizeNum = s => {
              const en = s.replace(/[٠١٢٣٤٥٦٧٨٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));
              const n = parseFloat(en.replace(/,/g, ''));
              return isNaN(n) ? s.trim().toLowerCase() : Math.round(n * 100) / 100;
            };
            const ns = normalizeNum(student), nc = normalizeNum(correct);
            const isCorrect = ns === nc ||
              (typeof ns === 'number' && typeof nc === 'number' && Math.abs(ns - nc) < 0.01) ||
              student === correct || correct.includes(student) || student.includes(correct);
            if (IS_BATCH_EXAM) {
              handleScrollAnswer(qIdx, val, null);
            } else {
              handleScrollAnswer(qIdx, val, isCorrect);
            }
          }

          return (
            <div key={qIdx} style={{ ...S.card, boxShadow: `0 4px 20px rgba(${themeRgb},.12)`, maxWidth: 680, margin: '0 auto 20px' }}>

              {/* Card header */}
              <div style={{ background: cardHeaderBg, borderRadius: '24px 24px 0 0', padding: '18px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ background: 'rgba(255,255,255,.2)', borderRadius: 99, padding: '4px 14px', color: '#fff', fontWeight: 700, fontSize: 14 }}>
                    {isAr ? `السؤال ${qIdx + 1}` : `Q ${qIdx + 1}`}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ ...S.pill, background: 'rgba(255,255,255,.2)', color: '#fff', fontSize: 11 }}>
                      {QUESTION_TYPE_LABELS[delivery_language]?.[question.question_type || 'MCQ'] || question.question_type || 'MCQ'}
                    </span>
                    {isSubmitted && (
                      <span style={{ ...S.pill, background: 'rgba(255,255,255,.25)', color: '#fff', fontSize: 15, fontWeight: 800 }}>
                        {IS_BATCH_EXAM ? '✓' : (isQCorrect ? '✓' : '✗')}
                      </span>
                    )}
                  </div>
                </div>
                {qLoText && (
                  <div style={{ marginTop: 8, fontSize: 11, color: 'rgba(255,255,255,.8)', fontStyle: 'italic' }}>
                    {qLoText}
                  </div>
                )}
              </div>

              {/* Card body */}
              <div className="qs-card-body" style={{ padding: '24px 24px 16px', background: '#fff', borderRadius: '0 0 24px 24px' }}>
                {/* Phase 1B-2b: reading passage (rendered when source_text present) */}
                {question.source_text && question.source_text.trim() && (
                  <div
                    className="qs-passage-block"
                    style={{
                      background: '#F9FAFB',
                      border: `1px solid ${theme.light || '#E5E7EB'}`,
                      borderInlineStart: `4px solid ${theme.color || '#6366F1'}`,
                      borderRadius: 12,
                      padding: '14px 16px',
                      marginBottom: 16,
                      maxHeight: 260,
                      overflowY: 'auto',
                      fontSize: 15,
                      lineHeight: 1.7,
                      color: '#374151',
                      direction: detectDir(question.source_text),
                      fontFamily: "'Sora',sans-serif",
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 12, color: theme.color || '#6366F1', marginBottom: 8, letterSpacing: 0.3 }}>
                      {isAr ? '📖 نص القراءة' : '📖 Reading passage'}
                    </div>
                    {lang === 'ar' ? cleanArabicText(question.source_text) : question.source_text}
                  </div>
                )}
                <p className="qs-question-text" style={{ fontSize: 17, fontWeight: 600, color: '#111827', lineHeight: 1.65, marginBottom: 20, direction: detectDir(question.question_text), fontFamily: "'Sora',sans-serif" }}>
                  {qDisplayTxt}
                </p>

                {/* Visual */}
                {(() => {
                  const _fallbackDesc = question.visual_text_fallback || question.image_description || '';
                  const _fallbackLabel = isAr ? 'وصف الصورة:' : 'Image description:';
                  const _fallbackBox = _fallbackDesc ? (
                    <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 12, padding: 12, marginBottom: 16, fontSize: 13, color: '#374151', direction: detectDir(_fallbackDesc) }}>
                      <span style={{ fontWeight: 700, marginInlineEnd: 6 }}>📊 {_fallbackLabel}</span>
                      {_fallbackDesc}
                    </div>
                  ) : null;

                  if (question.diagram_svg) return (
                    <div style={{ marginBottom: 16, borderRadius: 12, overflow: 'hidden', border: `1px solid ${theme.light}` }}
                      dangerouslySetInnerHTML={{ __html: question.diagram_svg }} />
                  );
                  if (question.visual_url) return (
                    <>
                      <img
                        src={question.visual_url}
                        alt={question.image_description || (isAr ? 'صورة توضيحية' : 'illustration')}
                        style={{ width: '100%', borderRadius: 12, marginBottom: 16 }}
                        onError={e => {
                          e.target.style.display = 'none';
                          const fb = e.target.nextSibling;
                          if (fb) fb.style.display = 'block';
                        }}
                      />
                      <div style={{ display: 'none', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 12, padding: 12, marginBottom: 16, fontSize: 13, color: '#374151', direction: detectDir(_fallbackDesc) }}>
                        <span style={{ fontWeight: 700, marginInlineEnd: 6 }}>📊 {_fallbackLabel}</span>
                        {_fallbackDesc}
                      </div>
                    </>
                  );
                  if (question.visual_text_fallback || (question.visual_attempted && question.image_description)) return _fallbackBox;
                  return null;
                })()}

                {/* TF */}
                {question.question_type === 'TF' && (
                  <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                    {['True', 'False'].map(choice => {
                      const label = isAr ? (choice === 'True' ? 'صح' : 'خطأ') : choice;
                      const correctAns = (question.correct_answer || '').toString().trim().toLowerCase();
                      const isThisCorrect = choice === 'True'
                        ? ['true','صح','yes','correct'].includes(correctAns)
                        : ['false','خطأ','no','incorrect'].includes(correctAns);
                      const state = isSubmitted
                        ? IS_BATCH_EXAM
                          ? (choice === selectedAns ? 'selected' : 'idle')
                          : (choice === selectedAns
                              ? (isQCorrect ? 'correct' : 'wrong')
                              : (!isQCorrect && isThisCorrect ? 'correct-reveal' : 'idle'))
                        : 'idle';
                      return (
                        <div key={choice} onClick={() => {
                          if (isSubmitted) return;
                          const ca = (question.correct_answer || '').toString().trim().toLowerCase();
                          const ok = choice.toLowerCase() === ca ||
                            (choice === 'True' && ['true','صح','yes','correct'].includes(ca)) ||
                            (choice === 'False' && ['false','خطأ','no','incorrect'].includes(ca));
                          handleScrollAnswer(qIdx, choice, IS_BATCH_EXAM ? null : ok);
                        }} style={{
                          flex: 1, padding: '18px 0', borderRadius: 16, textAlign: 'center',
                          fontWeight: 700, fontSize: 16, cursor: isSubmitted ? 'default' : 'pointer',
                          background: state === 'correct' || state === 'correct-reveal' ? '#059669' : state === 'wrong' ? '#DC2626' : state === 'selected' ? '#3B82F6' : theme.light,
                          color: state !== 'idle' ? '#fff' : theme.dark,
                          border: `2px solid ${state === 'correct' || state === 'correct-reveal' ? '#059669' : state === 'wrong' ? '#DC2626' : state === 'selected' ? '#3B82F6' : theme.primary}`,
                          transition: 'all 0.2s',
                        }}>
                          {label} {state === 'selected' || state === 'correct' || state === 'correct-reveal' ? '✓' : state === 'wrong' ? '✗' : ''}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* MAT — dropdown matching UI */}
                {question.question_type === 'MAT' && (() => {
                  const colA = question.column_a || [];
                  const colB = question.column_b || [];
                  // fibVals[qIdx] stores the selections map as JSON: {"aText": "bText"}
                  const currentSelections = (() => {
                    try {
                      const parsed = fibVal ? JSON.parse(fibVal) : {};
                      return (typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
                    } catch { return {}; }
                  })();
                  const filledCount = colA.filter(a => currentSelections[a]).length;

                  function updateMatch(aText, bText) {
                    const updated = { ...currentSelections, [aText]: bText };
                    setFibVals(prev => ({ ...prev, [qIdx]: JSON.stringify(updated) }));
                    if (colA.every(a => updated[a])) {
                      const arrayForm = JSON.stringify(colA.map(a => ({ a, b: updated[a] })));
                      handleScrollAnswer(qIdx, arrayForm, IS_BATCH_EXAM ? null : null);
                    }
                  }

                  return (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1.5fr', gap: 10, alignItems: 'center', padding: 12, background: '#F9FAFB', borderRadius: 10 }}>
                        {colA.map((aItem, i) => (
                          <React.Fragment key={i}>
                            <div style={{ padding: '10px 12px', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 14, color: '#1F2937', direction: 'rtl' }}>
                              {i + 1}. {aItem}
                            </div>
                            <div style={{ color: '#9CA3AF', fontSize: 18, textAlign: 'center' }}>←</div>
                            <select
                              value={currentSelections[aItem] || ''}
                              onChange={e => !isSubmitted && updateMatch(aItem, e.target.value)}
                              disabled={isSubmitted}
                              style={{ padding: '10px 12px', border: `1px solid ${currentSelections[aItem] ? theme.primary : '#D1D5DB'}`, borderRadius: 8, fontSize: 14, background: '#fff', color: '#1F2937', fontFamily: 'inherit', direction: 'rtl', cursor: isSubmitted ? 'not-allowed' : 'pointer', width: '100%' }}
                            >
                              <option value="">{isAr ? '-- اختر --' : '-- Select --'}</option>
                              {colB.map((bItem, j) => (
                                <option key={j} value={bItem}>{bItem}</option>
                              ))}
                            </select>
                          </React.Fragment>
                        ))}
                      </div>
                      {!isSubmitted && filledCount < colA.length && (
                        <p style={{ textAlign: 'center', fontSize: 12, color: '#6B7280', marginTop: 8 }}>
                          {isAr ? `اختر المطابق لكل عنصر (${filledCount}/${colA.length})` : `Match all items (${filledCount}/${colA.length})`}
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* POEM_FIB — per-verse fill-in-blank */}
                {question.question_type === 'POEM_FIB' && (() => {
                  const verses = question.verses || [];
                  const currentAnswers = (() => {
                    try { return JSON.parse(fibVals[qIdx] || '[]'); } catch { return []; }
                  })();
                  const answerMap = {};
                  (Array.isArray(currentAnswers) ? currentAnswers : []).forEach(a => {
                    if (a && a.verse_number != null) answerMap[a.verse_number] = a.answer || '';
                  });

                  function updateVerseAnswer(verseNum, text) {
                    const updated = { ...answerMap, [verseNum]: text };
                    const arrayForm = verses.map(v => ({
                      verse_number: v.verse_number,
                      answer: updated[v.verse_number] || '',
                    }));
                    setFibVals(prev => ({ ...prev, [qIdx]: JSON.stringify(arrayForm) }));
                  }

                  function submitPoemFib() {
                    const arrayForm = verses.map(v => ({
                      verse_number: v.verse_number,
                      answer: answerMap[v.verse_number] || '',
                    }));
                    if (!arrayForm.every(a => a.answer.trim().length > 0)) return;
                    handleScrollAnswer(qIdx, JSON.stringify(arrayForm), null);
                  }

                  const allFilled = verses.every(v => (answerMap[v.verse_number] || '').trim().length > 0);

                  return (
                    <div style={{ padding: '12px 0' }}>
                      <div style={{ background: '#F9FAFB', borderRadius: 12, padding: 16, border: '1px solid #E5E7EB' }}>
                        {verses.map((v, idx) => (
                          <div key={v.verse_number} style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: idx < verses.length - 1 ? 12 : 0, direction: 'rtl' }}>
                            <div style={{ flex: '1 1 45%', padding: '10px 14px', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, fontSize: 15, color: '#1F2937', lineHeight: 1.7 }}>
                              {v.visible_text}
                            </div>
                            <div style={{ color: '#9CA3AF', fontSize: 20 }}>⇐</div>
                            <input
                              type="text"
                              value={answerMap[v.verse_number] || ''}
                              onChange={e => updateVerseAnswer(v.verse_number, e.target.value)}
                              disabled={isSubmitted}
                              placeholder="أكمل البيت..."
                              style={{ flex: '1 1 45%', padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: 10, fontSize: 15, fontFamily: 'inherit', direction: 'rtl', textAlign: 'right', outline: 'none', background: isSubmitted ? '#F9FAFB' : '#fff' }}
                            />
                          </div>
                        ))}
                      </div>
                      {!isSubmitted && (
                        <button
                          onClick={submitPoemFib}
                          disabled={!allFilled}
                          style={{ width: '100%', padding: 12, background: allFilled ? 'linear-gradient(135deg,#7C3AED,#A78BFA)' : '#E5E7EB', color: allFilled ? '#fff' : '#9CA3AF', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: allFilled ? 'pointer' : 'not-allowed', marginTop: 12, fontFamily: 'inherit' }}
                        >
                          تأكيد الإجابة
                        </button>
                      )}
                    </div>
                  );
                })()}

                {/* POEM_MAT — verse ordering via position dropdowns */}
                {question.question_type === 'POEM_MAT' && (() => {
                  const verses = question.verses_shuffled || [];
                  const currentAnswers = (() => {
                    try { return JSON.parse(fibVals[qIdx] || '[]'); } catch { return []; }
                  })();
                  const positionMap = {};
                  (Array.isArray(currentAnswers) ? currentAnswers : []).forEach(a => {
                    if (a && a.verse_number != null && a.position != null) positionMap[a.verse_number] = a.position;
                  });

                  const totalVerses = verses.length;
                  const allPositions = Array.from({ length: totalVerses }, (_, i) => i + 1);

                  function getVerseText(v) { return typeof v === 'string' ? v : (v.verse_text || ''); }
                  function getVerseNumber(v, fallbackIdx) { return typeof v === 'object' && v.verse_number != null ? v.verse_number : fallbackIdx + 1; }

                  function updatePosition(vNum, pos) {
                    const newPos = pos === '' ? null : parseInt(pos, 10);
                    const updated = { ...positionMap };
                    if (newPos === null) {
                      delete updated[vNum];
                    } else {
                      Object.keys(updated).forEach(k => { if (updated[k] === newPos) delete updated[k]; });
                      updated[vNum] = newPos;
                    }
                    const arrayForm = Object.entries(updated).map(([vn, p]) => ({ position: p, verse_number: parseInt(vn, 10) }));
                    const stringified = JSON.stringify(arrayForm);
                    setFibVals(prev => ({ ...prev, [qIdx]: stringified }));
                    if (arrayForm.length === totalVerses) handleScrollAnswer(qIdx, stringified, null);
                  }

                  const filledCount = Object.keys(positionMap).length;

                  return (
                    <div style={{ padding: '12px 0' }}>
                      <div style={{ background: '#F9FAFB', borderRadius: 12, padding: 16, border: '1px solid #E5E7EB' }}>
                        {verses.map((v, idx) => {
                          const vNum = getVerseNumber(v, idx);
                          const vText = getVerseText(v);
                          const currentPos = positionMap[vNum] || '';
                          return (
                            <div key={idx} style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: idx < verses.length - 1 ? 12 : 0, direction: 'rtl' }}>
                              <select
                                value={currentPos}
                                onChange={e => !isSubmitted && updatePosition(vNum, e.target.value)}
                                disabled={isSubmitted}
                                style={{ flex: '0 0 72px', padding: '10px 8px', border: '1px solid #D1D5DB', borderRadius: 10, fontSize: 15, background: '#fff', cursor: isSubmitted ? 'not-allowed' : 'pointer', textAlign: 'center', fontFamily: 'inherit' }}
                              >
                                <option value="">--</option>
                                {allPositions.map(p => {
                                  const takenByAnother = Object.entries(positionMap).some(([k, val]) => val === p && parseInt(k, 10) !== vNum);
                                  return <option key={p} value={p} disabled={takenByAnother}>{p}</option>;
                                })}
                              </select>
                              <div style={{ color: '#9CA3AF', fontSize: 20 }}>→</div>
                              <div style={{ flex: 1, padding: '10px 14px', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, fontSize: 15, color: '#1F2937', lineHeight: 1.7 }}>
                                {vText}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {!isSubmitted && filledCount < totalVerses && (
                        <p style={{ textAlign: 'center', fontSize: 13, color: '#6B7280', marginTop: 10 }}>
                          حدد ترتيب كل بيت ({filledCount}/{totalVerses})
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* FIB / CALC / open-ended text types */}
                {(question.question_type === 'FIB' || question.question_type === 'CALC' || OPEN_TYPES.includes(question.question_type) || (question.question_type === 'VIS' && !(question.answer_options?.length > 0))) && (
                  <div style={{ marginTop: 8 }}>
                    {question.question_type === 'USE_SENTENCE' && question.target_word && (
                      <div style={{ marginBottom: 14, textAlign: 'center' }}>
                        <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 6, direction: 'rtl' }}>
                          {isAr ? 'الكلمة المطلوبة:' : 'Target Word:'}
                        </p>
                        <div style={{ display: 'inline-block', padding: '10px 32px', borderRadius: 12, border: '2px solid #059669', background: '#D1FAE5', fontSize: 22, fontWeight: 700, color: '#065F46', direction: 'rtl' }}>
                          {question.target_word}
                        </div>
                      </div>
                    )}
                    {question.question_type === 'WA' && question.word_guidance && (
                      <div style={{ marginBottom: 10, padding: '8px 14px', borderRadius: 10, background: '#ECFDF5', border: '1px solid #059669', direction: isAr ? 'rtl' : 'ltr' }}>
                        <span style={{ fontSize: 13, color: '#059669', fontWeight: 600 }}>
                          {isAr ? `الكتابة المتوقَّعة: ${question.word_guidance}` : `Expected writing: ${question.word_guidance}`}
                        </span>
                      </div>
                    )}
                    {question.question_type === 'CALC' && question.answer_format_example && (
                      <div style={{ marginBottom: 10, padding: '8px 14px', borderRadius: 10, background: '#EFF6FF', border: '1px solid #3B82F6', direction: 'rtl' }}>
                        <span style={{ fontSize: 13, color: '#1D4ED8', fontWeight: 600 }}>
                          {question.answer_format_example}
                        </span>
                      </div>
                    )}
                    {OPEN_TYPES.includes(question.question_type) ? (
                      <textarea value={fibVal} rows={4} disabled={isSubmitted}
                        onChange={e => !isSubmitted && setFibVals(prev => ({ ...prev, [qIdx]: e.target.value }))}
                        placeholder={isAr ? 'اكتب إجابتك هنا...' : 'Type your answer here...'}
                        style={{ width: '100%', padding: '14px 16px', borderRadius: 12, fontSize: 15, border: `2px solid ${isSubmitted && !IS_BATCH_EXAM ? (isQCorrect ? '#059669' : '#DC2626') : theme.primary}`, outline: 'none', direction: isAr ? 'rtl' : 'ltr', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', background: isSubmitted && !IS_BATCH_EXAM ? (isQCorrect ? '#D1FAE5' : '#FEE2E2') : '#fff' }} />
                    ) : (
                      <input type="text" inputMode={question.question_type === 'CALC' ? 'decimal' : 'text'}
                        value={fibVal} disabled={isSubmitted}
                        onChange={e => !isSubmitted && setFibVals(prev => ({ ...prev, [qIdx]: e.target.value }))}
                        onKeyDown={e => e.key === 'Enter' && submitFib(fibVal)}
                        placeholder={isAr
                          ? (question.question_type === 'CALC' ? 'اكتب الناتج هنا...' : 'اكتب إجابتك هنا...')
                          : (question.question_type === 'CALC' ? 'Enter your answer...' : 'Type your answer here...')}
                        style={{ width: '100%', padding: '14px 16px', borderRadius: 12, fontSize: 15, border: `2px solid ${isSubmitted && !IS_BATCH_EXAM ? (isQCorrect ? '#059669' : '#DC2626') : theme.primary}`, outline: 'none', direction: isAr ? 'rtl' : 'ltr', boxSizing: 'border-box', background: isSubmitted && !IS_BATCH_EXAM ? (isQCorrect ? '#D1FAE5' : '#FEE2E2') : '#fff' }} />
                    )}
                    {!isSubmitted && (
                      <button onClick={() => submitFib(fibVal)} disabled={!fibVal.trim()}
                        style={{ marginTop: 10, width: '100%', padding: '12px 0', borderRadius: 12, background: fibVal.trim() ? theme.gradient : '#E5E7EB', color: fibVal.trim() ? '#fff' : '#9CA3AF', border: 'none', fontWeight: 700, fontSize: 15, cursor: fibVal.trim() ? 'pointer' : 'default' }}>
                        {isAr ? 'تأكيد الإجابة' : 'Submit Answer'}
                      </button>
                    )}
                    {isSubmitted && !IS_BATCH_EXAM && !OPEN_TYPES.includes(question.question_type) && (
                      <div style={{ marginTop: 8, fontSize: 13, color: '#6B7280', direction: isAr ? 'rtl' : 'ltr' }}>
                        {isAr ? 'الإجابة الصحيحة: ' : 'Correct answer: '}
                        {/* CALC: show final_answer (bare answer) not full working paragraph */}
                        <strong>{question.question_type === 'CALC'
                          ? (question.final_answer || question.correct_answer)
                          : question.correct_answer}</strong>
                      </div>
                    )}
                  </div>
                )}

                {/* MCQ options */}
                {(!question.question_type || question.question_type === 'MCQ' || (question.question_type === 'VIS' && question.answer_options?.length > 0)) && qOpts.map(opt => {
                  const letter   = opt.split('.')[0].trim();
                  const text     = opt.split('. ').slice(1).join('. ');
                  const arLetter = AR_LETTERS[letter] || letter;
                  const state    = qOptState(letter);
                  return (
                    <div key={letter} className="qs-option" style={qOptStyle(letter)}
                      onClick={() => {
                        if (isSubmitted) return;
                        handleScrollAnswer(qIdx, letter, IS_BATCH_EXAM ? null : letter === correctLetter);
                      }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: state === 'correct' || state === 'correct-reveal' ? '#059669' : state === 'wrong' ? '#DC2626' : theme.light, color: ['correct','correct-reveal','wrong'].includes(state) ? '#fff' : theme.dark, fontWeight: 700, fontSize: 15 }}>
                        {isAr ? arLetter : letter}
                      </div>
                      <span style={{ fontSize: 15, color: '#1F2937', flex: 1, direction: isAr ? 'rtl' : 'ltr', fontFamily: "'Nunito','Noto Kufi Arabic',sans-serif" }}>
                        {text}
                      </span>
                      {!IS_BATCH_EXAM && (state === 'correct' || state === 'correct-reveal') && <span style={{ fontSize: 18 }}>✓</span>}
                      {!IS_BATCH_EXAM && state === 'wrong' && <span style={{ fontSize: 18 }}>✗</span>}
                    </div>
                  );
                })}

                {/* Per-question feedback */}
                {isSubmitted && IS_BATCH_EXAM && (
                  <div style={{ marginTop: 16, padding: '14px 18px', borderRadius: 16, background: '#F0F9FF', border: '2px solid #BAE6FD', direction: isAr ? 'rtl' : 'ltr' }}>
                    <div style={{ fontWeight: 700, color: '#1E3A5F' }}>
                      ✓ {isAr ? 'تم تسجيل إجابتك' : 'Answer recorded'}
                    </div>
                  </div>
                )}
                {isSubmitted && !IS_BATCH_EXAM && (
                  <div style={{ marginTop: 16, padding: '14px 18px', borderRadius: 16, background: isQCorrect ? 'linear-gradient(135deg,#D1FAE5,#A7F3D0)' : 'linear-gradient(135deg,#FEE2E2,#FECACA)', border: `2px solid ${isQCorrect ? '#6EE7B7' : '#FCA5A5'}`, boxShadow: isQCorrect ? '0 3px 0 #6EE7B7' : '0 3px 0 #FCA5A5', direction: isAr ? 'rtl' : 'ltr' }}>
                    {isQCorrect ? (
                      <div style={{ fontWeight: 700, color: '#065F46' }}>
                        ✓ {isAr ? 'إجابة صحيحة! أحسنت 🎉' : 'Correct! Well done 🎉'}
                      </div>
                    ) : (
                      <>
                        <div style={{ fontWeight: 700, color: '#7F1D1D', marginBottom: 4 }}>
                          ✗ {isAr ? 'إجابة خاطئة' : 'Incorrect'}
                        </div>
                        <div style={{ fontSize: 13, color: '#991B1B' }}>
                          {qWae[selectedAns] || (isAr ? 'راجع الشرح' : 'Review the explanation')}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── J. BOTTOM BAR ── */}
      <div className="qs-bottom-bar" style={S.bottomBar}>
        <span style={{ fontSize: 14, color: '#6B7280' }}>
          {isAr ? `${answeredCount} من ${totalQ} سؤال` : `${answeredCount} of ${totalQ} answered`}
        </span>
        <button
          onClick={handleScrollFinish}
          disabled={answeredCount < totalQ || grading}
          style={{ ...S.nextBtn, background: answeredCount >= totalQ ? theme.gradient : '#E5E7EB', color: answeredCount >= totalQ ? '#fff' : '#9CA3AF', cursor: answeredCount >= totalQ && !grading ? 'pointer' : 'default', opacity: grading ? 0.7 : 1 }}>
          {grading ? (isAr ? 'جاري التقييم...' : 'Grading...') : (isAr ? 'إنهاء الاختبار ✓' : 'Finish Quiz ✓')}
        </button>
      </div>

      {/* Grading overlay */}
      {grading && (
        <div style={S.gradingOverlay}>
          <div style={S.gradingBox}>
            <div style={{ width: 56, height: 56, border: '4px solid #E5E7EB', borderTopColor: '#059669', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
            <div style={{ fontWeight: 700, fontSize: 17, color: '#1F2937', marginBottom: 6 }}>
              {isAr ? 'جاري تصحيح إجاباتك...' : 'Grading your answers...'}
            </div>
            {IS_BATCH_EXAM && (
              <div style={{ fontSize: 13, color: '#6B7280' }}>
                {isAr ? 'قد يستغرق الأمر بضع ثوانٍ' : 'This may take a few seconds'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Styles
───────────────────────────────────────────────────────────────────────── */
function hexToRgb(h) {
  const r = parseInt(h.slice(1,3),16);
  const g = parseInt(h.slice(3,5),16);
  const b = parseInt(h.slice(5,7),16);
  return `${r},${g},${b}`;
}

const S = {
  page: {
    minHeight: '100vh',
    background: 'transparent',
    display: 'flex', flexDirection: 'column',
    fontFamily: "'Sora','Noto Kufi Arabic',sans-serif",
    position: 'relative', overflow: 'hidden',
  },
  canvas: {
    position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
  },
  header: {
    position: 'sticky', top: 0, zIndex: 100,
    background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(229,231,235,0.8)',
    boxShadow: '0 2px 12px rgba(0,0,0,.05)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 20px', gap: 12,
  },
  logo: { fontFamily: "'Sora',sans-serif", fontSize: 22, display: 'flex', gap: 1 },
  chips: { display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1, justifyContent: 'center' },
  chip: { borderRadius: 99, padding: '4px 12px', fontSize: 12, fontWeight: 600 },
  avatar: {
    width: 34, height: 34, borderRadius: '50%',
    background: 'linear-gradient(135deg,#059669,#1D4ED8)',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 13,
  },
  exitBtn: {
    border: '2px solid #E5E7EB', background: '#fff', borderRadius: 12,
    padding: '6px 12px', cursor: 'pointer', fontSize: 12, color: '#6B7280',
    boxShadow: '0 2px 0 #E5E7EB', transition: 'transform .08s',
  },
  strip: {
    position: 'sticky', top: 57, zIndex: 90,
    background: 'rgba(255,255,255,.85)', backdropFilter: 'blur(8px)',
    borderBottom: '1px solid #F3F4F6',
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '10px 20px',
  },
  card: {
    width: '100%', maxWidth: 680,
    background: 'white',
    borderRadius: '18px',
    border: '1px solid #E5E7EB',
    backdropFilter: 'blur(8px)',
    zIndex: 1,
  },
  bottomBar: {
    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
    background: 'rgba(255,255,255,.95)', backdropFilter: 'blur(12px)',
    borderTop: '1px solid #E5E7EB',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 24px',
  },
  nextBtn: {
    border: 'none', borderRadius: 12,
    padding: '12px 28px', color: '#fff',
    fontSize: 15, fontWeight: 700, cursor: 'pointer',
  },
  gradingOverlay: {
    position: 'fixed', inset: 0, zIndex: 200,
    background: 'rgba(0,0,0,.4)', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
  },
  gradingBox: {
    background: '#fff', borderRadius: 20, padding: '36px 48px',
    textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,.2)',
  },
  /* Results */
  resultsWrap: {
    maxWidth: 640, margin: '0 auto', padding: '0 16px 60px',
    display: 'flex', flexDirection: 'column', gap: 16, zIndex: 1,
  },
  heroBanner: {
    borderRadius: '0 0 28px 28px', padding: '36px 24px 60px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
  },
  heroEmoji: { fontSize: 56 },
  heroTitle:  { fontSize: 22, fontWeight: 800, color: '#fff', textAlign: 'center' },
  scoreRing:  { position: 'relative', width: 120, height: 120 },
  scoreRingInner: {
    position: 'absolute', inset: 0, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontSize: 26, fontWeight: 800, color: '#fff',
  },
  statsPanel: {
    display: 'flex', gap: 12, marginTop: -36,
    background: '#fff', borderRadius: 24,
    border: '2px solid #E5E7EB',
    padding: '20px 24px',
    boxShadow: '0 5px 0 #E5E7EB, 0 12px 32px rgba(0,0,0,.07)',
  },
  statItem: {
    flex: 1, textAlign: 'center', padding: '12px 0',
    borderRadius: 16, background: '#FAFAFA',
  },
  xpCard: {
    background: 'linear-gradient(135deg,#FEF3C7,#FDE68A)',
    border: '2px solid #FCD34D', borderRadius: 20,
    padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12,
    boxShadow: '0 4px 0 #FCD34D, 0 8px 20px rgba(217,119,6,.12)',
  },
  xpBadge: {
    background: 'linear-gradient(135deg,#F59E0B,#D97706)',
    color: '#fff', borderRadius: 99, padding: '6px 16px',
    fontWeight: 800, fontSize: 18,
  },
  reviewCard: {
    background: '#fff', border: '2px solid #E5E7EB',
    borderRadius: 20, padding: '16px 20px',
    boxShadow: '0 4px 0 #E5E7EB, 0 8px 20px rgba(0,0,0,.05)',
  },
  reviewToggle: {
    border: 'none', borderRadius: 10, padding: '10px 18px',
    cursor: 'pointer', fontWeight: 600, fontSize: 14, width: '100%',
  },
  reviewItem: { marginTop: 14, paddingTop: 14, borderTop: '1px solid #F3F4F6' },
  reviewQ: { fontSize: 14, color: '#374151', fontWeight: 500 },
  pill: { borderRadius: 99, padding: '3px 10px', fontSize: 12, fontWeight: 600 },
  actionBtns: { display: 'flex', flexDirection: 'column', gap: 10 },
  primaryBtn: {
    border: 'none', borderRadius: 18, padding: '16px',
    color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer',
    transition: 'transform .08s, box-shadow .08s',
  },
  secondaryBtn: {
    border: '2px solid #E5E7EB', background: '#fff',
    borderRadius: 18, padding: '14px',
    color: '#374151', fontSize: 15, fontWeight: 600, cursor: 'pointer',
    boxShadow: '0 4px 0 #E5E7EB', transition: 'transform .08s, box-shadow .08s',
  },
};

/* ─── Global CSS (animations + fonts) ──────────────────────────────────── */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Nunito:wght@400;600;700&family=Noto+Kufi+Arabic:wght@400;600;700;800&display=swap');

@keyframes qs-enter  { from { opacity:0; transform:translateX(50px) } to { opacity:1; transform:translateX(0) } }
@keyframes qs-exit   { from { opacity:1; transform:translateX(0) } to { opacity:0; transform:translateX(-50px) } }
@keyframes qs-shake  { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)} 40%{transform:translateX(8px)} 60%{transform:translateX(-6px)} 80%{transform:translateX(6px)} }
@keyframes qs-shimmer{ 0%{background-position:200% center} 100%{background-position:-200% center} }
@keyframes qs-blink  { 0%,100%{opacity:1} 50%{opacity:0.4} }
@keyframes qs-burst  {
  0%   { transform: translate(0,0) scale(1); opacity:1; }
  100% { transform: translate(var(--bx),var(--by)) scale(0); opacity:0; }
}
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 767px) {
  .qs-content-col {
    max-width: 100% !important;
    padding: 12px 12px 100px !important;
  }
  .qs-card {
    border-radius: 12px !important;
  }
  .qs-card-body {
    padding: 16px 16px 12px !important;
  }
  .qs-question-text {
    font-size: 15px !important;
  }
  .qs-strip {
    padding: 8px 12px !important;
    font-size: 13px !important;
    gap: 10px !important;
  }
  .qs-option {
    min-height: 48px !important;
    font-size: 14px !important;
    padding: 10px 14px !important;
  }
  .qs-mat-cols {
    flex-direction: column !important;
  }
  .qs-bottom-bar {
    padding: 10px 12px !important;
    flex-wrap: wrap !important;
    gap: 8px !important;
  }
  .qs-bottom-bar button {
    width: 100% !important;
    margin-bottom: 8px !important;
  }
  .qs-strip span {
    font-size: 12px !important;
  }
}

@media (min-width: 768px) and (max-width: 1024px) {
  .qs-content-col {
    max-width: 640px !important;
    padding: 20px 20px 100px !important;
  }
  .qs-option {
    min-height: 52px !important;
  }
  .qs-mat-cols {
    flex-wrap: wrap !important;
  }
  .qs-mat-cols > * {
    min-width: 140px !important;
  }
}
`;
